import { css } from '@emotion/react';
import { useRouter } from 'next/router';

import { addParamsToUrl } from '@zapier/url-utils';
import {
  Colors,
  Field,
  Link,
  TextInput,
  Typography,
} from '@zapier/design-system';
import { urls, useSize } from '@zapier/identity-utils';
import { AuthButton } from '@zapier/authentication';

import { useResetPassword } from './useResetPassword';
import {
  AuthLayout,
  CompaniesIntroPromo,
} from '@zapier/auth-pages/src/components/global';
import { parseHash } from '@zapier/auth-pages/src/components/pages/ResetPasswordPage/parseHash';

const errorStyle = css`
  ${Typography.base}
  color: ${Colors.error};
`;

const rootStyle = css`
  display: grid;
  grid-gap: 25px;
`;

const successStyle = css`
  ${Typography.base}
  color: ${Colors.success};
`;

export function ResetPasswordPage() {
  const size = useSize();
  const router = useRouter();

  const hashPart = router.asPath.split('#')?.[1];
  const hash = (hashPart || router.query?.hash || '').toString();
  const { username } = parseHash(hash);

  const { canSubmit, error, formData, handleChange, handleSubmit, status } =
    useResetPassword({ hash });

  const title = username
    ? `Set the password for ${username}`
    : 'Set your password';

  return (
    <AuthLayout
      promo={<CompaniesIntroPromo />}
      showPromo={size !== 'small'}
      title={title}
      titlePosition={'right'}
    >
      <form css={rootStyle} onSubmit={handleSubmit}>
        <Field
          label="Password"
          inputId="reset-password-1"
          isRequired={true}
          renderInput={inputProps => (
            <TextInput
              {...inputProps}
              autoComplete="new-password"
              isDisabled={status === 'submitting'}
              name="password1"
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                handleChange('password1', e.target.value)
              }
              placeholder="Password"
              type="password"
              value={formData.password1}
            />
          )}
        />

        <Field
          label="Confirm password"
          inputId="reset-password-2"
          isRequired={true}
          renderInput={inputProps => (
            <TextInput
              {...inputProps}
              autoComplete="new-password"
              isDisabled={status === 'submitting'}
              name="password2"
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                handleChange('password2', e.target.value)
              }
              placeholder="Confirm password"
              type="password"
              value={formData.password2}
            />
          )}
        />
        <AuthButton
          disabled={!canSubmit || status !== 'initial'}
          isBusy={status === 'submitting'}
          type="submit"
        >
          Set new password
        </AuthButton>
        {error && <div css={errorStyle}>{error}</div>}
        {status === 'success' && (
          <div css={successStyle}>
            Password changed!{' '}
            <Link href={addParamsToUrl(urls.LOGIN_URL, { email: username })}>
              Click here
            </Link>{' '}
            to continue
          </div>
        )}
      </form>
    </AuthLayout>
  );
}
